import React, { useState, useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { $getNodeByKey } from 'lexical';

import { getImageUrl } from '@/src/utils';

// Decorator component for image node
function ResizableImage({
    nodeKey,
    src,
    alt,
    width,
    height,
}: {
    nodeKey: string;
    src: string;
    alt?: string;
    width: number;
    height: number;
}) {
    const [editor] = useLexicalComposerContext();
    const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);

    const resolvedSrc = src.startsWith('local://')
        ? getImageUrl(src.slice('local://'.length)) ||
          'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='
        : src;

    const [uiW, setUiW] = useState<number>(width);
    const [uiH, setUiH] = useState<number>(height);

    // Use refs to avoid stale state when resizing
    const wRef = useRef(uiW);
    const hRef = useRef(uiH);

    useEffect(() => {
        setUiW(width);
        setUiH(height);
        wRef.current = width;
        hRef.current = height;
    }, [width, height]);

    const onClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isSelected) {
            clearSelection();
            setSelected(true);
        }
    };

    const onMouseDownResize = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        const startY = e.clientY;
        const startW = uiW;
        const startH = uiH;
        const ratio = startW / Math.max(1, startH);
        const MIN = 20;

        let raf = 0;

        const applySize = (newW: number, newH: number) => {
            wRef.current = newW;
            hRef.current = newH;
            setUiW(newW);
            setUiH(newH);
        };

        const onMove = (moveEvent: MouseEvent) => {
            const dw = moveEvent.clientX - startX;
            const dh = moveEvent.clientY - startY;

            const freeform = !!moveEvent.shiftKey;
            let newW = startW;
            let newH = startH;

            if (freeform) {
                newW = Math.max(MIN, Math.round(startW + dw));
                newH = Math.max(MIN, Math.round(startH + dh));
            } else {
                if (Math.abs(dw) >= Math.abs(dh)) {
                    newW = Math.max(MIN, Math.round(startW + dw));
                    newH = Math.max(MIN, Math.round(newW / ratio));
                } else {
                    newH = Math.max(MIN, Math.round(startH + dh));
                    newW = Math.max(MIN, Math.round(newH * ratio));
                }
            }

            if (raf === 0) {
                raf = requestAnimationFrame(() => {
                    raf = 0;
                    applySize(newW, newH);
                });
            }
        };

        const onUp = () => {
            if (raf) cancelAnimationFrame(raf);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);

            const finalW = wRef.current;
            const finalH = hRef.current;

            editor.update(() => {
                const node = $getNodeByKey(nodeKey);
                if (node && 'setDimensions' in node) {
                    (node as any).setDimensions(finalW, finalH);
                }
            });
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    return (
        <div
            style={{
                position: 'relative',
                display: 'inline-block',
                outline: isSelected ? '2px solid #4A90E2' : 'none',
                outlineOffset: 2,
            }}
            onClick={onClick}
        >
            <img
                src={resolvedSrc}
                alt={alt ?? ''}
                style={{ width: uiW, height: uiH, display: 'block' }}
                draggable={false}
            />
            <div
                onMouseDown={onMouseDownResize}
                style={{
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    width: 12,
                    height: 12,
                    background: 'rgba(0,0,0,0.5)',
                    cursor: 'nwse-resize',
                    borderRadius: '2px',
                }}
            />
        </div>
    );
}

export default ResizableImage;
